import { Button, CustomSelect, ValidateInput } from "@/shared";
import { MouseEvent, useMemo, useState } from "react";
import styles from "./EditUserForm.module.scss";
import {
  Controller,
  FormProvider,
  useForm,
  ControllerRenderProps,
  FieldValues,
} from "react-hook-form";
import { EditUserFieldsType, IUser } from "@/entities/users/types";
import { useAppDispatch, useAppSelector } from "@/shared/hooks";
import { editUser, fetchUsers } from "@/entities/users/slice";
import { ResetPasswordButton } from "@/features/resetPasswordButton/ResetPasswordButton";
import { useDrawer } from "@/app/providers/DrawerProvider/useDrawer";
import { fetchOneDealer } from "@/entities/dealer";
import { UserRolesEnum } from "@/app/model/types";

interface EditUserFormProps {
  currentUser: IUser;
  viewType: "skad" | "dealer";
}

const ROLE_MAP = {
  skad: {
    Admin: ["Admin", "Manager", "Marketer"] as const,
    Manager: [] as const,
    Dealer: [] as const,
    Sales: [] as const,
    ApiDealer: [] as const,
    Marketer: [] as const,
    TechAdmin: ["Admin", "Manager", "Marketer"] as const,
  },
  dealer: {
    Admin: ["Dealer", "Sales"] as const,
    Manager: ["Dealer", "Sales"] as const,
    Dealer: ["Dealer", "Sales"] as const,
    Sales: [] as const,
    Marketer: [] as const,
    ApiDealer: [] as const,
    TechAdmin: ["Dealer", "Sales", "ApiDealer"] as const,
  },
} as const;

const toSelectItems = (ids: readonly (keyof typeof UserRolesEnum)[]) =>
  ids.map((id) => ({
    id,
    name: UserRolesEnum[id],
  }));

export const EditUserForm: React.FC<EditUserFormProps> = ({
  currentUser,
  viewType,
}) => {
  const dispatch = useAppDispatch();
  const methods = useForm({ mode: "all" });
  const [showChangePass, setShowChangePass] = useState<boolean>(false);
  const [isApiDealer, setIsApiDealer] = useState<boolean>(false);

  const { roleName, isSkadUser, isTechAdmin } = useAppSelector((s) => s.viewer);
  const { currentDealer } = useAppSelector((s) => s.dealers);
  const { closeDrawer } = useDrawer();
  const { errors } = methods.formState;

  const roleOptions = useMemo(() => {
    if (!roleName) return [];

    const ids = ROLE_MAP[viewType][roleName] ?? [];

    return toSelectItems(ids);
  }, [viewType, roleName]);

  const [lastName = "", firstName = "", middleName = ""] = (() => {
    const parts = currentUser.fio?.split(" ") ?? [];
    return [parts[0] ?? "", parts[1] ?? "", parts[2] ?? ""];
  })();

  const canChangePassword = isTechAdmin && currentUser.roleName !== "TechAdmin";

  const changeHandler = (
    field: ControllerRenderProps<FieldValues, string>,
    value: string,
  ) => {
    console.log(value);
    if (value === "ApiDealer") {
      methods.clearErrors();
      setIsApiDealer(true);
    } else {
      setIsApiDealer(false);
    }
    field.onChange(value);
  };

  const resetForm = (e: MouseEvent) => {
    e.preventDefault();
    closeDrawer();
    methods.reset();
  };

  const onSubmit = async (data: Partial<EditUserFieldsType>) => {
    if (!data.role) data.role = currentUser.roleName;
    data.id = currentUser.id;
    if (data.password !== data.confirmPassword) {
      methods.setError("confirmPassword", {
        type: "validate",
        message: "Пароли не совпадают",
      });
      return;
    }
    const newUserData = { ...data };
    delete newUserData.confirmPassword;

    closeDrawer();
    await dispatch(editUser(newUserData));
    await dispatch(fetchUsers(""));

    if (isSkadUser && currentDealer) {
      await dispatch(fetchOneDealer(currentUser.dealerId));
    }

    methods.reset();
  };

  const handleShowChangePassword = () => {
    methods.resetField("password");
    methods.resetField("confirmPassword");
    setShowChangePass((v) => !v);
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(onSubmit)}
        className={styles.formWrapper}
      >
        {/* ---------- SELECT РОЛИ ---------- */}
        {roleOptions.length > 0 && (
          <Controller
            control={methods.control}
            name="role"
            render={({ field }) => (
              <CustomSelect
                items={roleOptions}
                placeholder="Выбор роли"
                /* Если текущая роль уже присутствует в списке – показываем её,
                   иначе селект будет пустым (но пользователь всё равно может её сменить). */
                fieldValue={
                  roleOptions.find((r) => r.id === currentUser.roleName)?.id ??
                  ""
                }
                handleChange={(value) =>
                  changeHandler(
                    field as ControllerRenderProps<FieldValues, string>,
                    value,
                  )
                }
              />
            )}
          />
        )}

        {/* ---------- ФИО ---------- */}
        <div className={styles.formRow}>
          <ValidateInput
            placeholder="Фамилия"
            name="lastName"
            required={!isApiDealer}
            nameRu="Фамилия"
            defaultValue={lastName}
          />
        </div>

        <div className={styles.formRow}>
          <ValidateInput
            placeholder="Имя"
            name="firstName"
            required={!isApiDealer}
            nameRu="Имя"
            defaultValue={firstName}
          />
        </div>

        <div className={styles.formRow}>
          <ValidateInput
            placeholder="Отчество"
            name="middleName"
            required={!isApiDealer}
            nameRu="Отчество"
            defaultValue={middleName}
          />
        </div>
        {canChangePassword && (
          <div className={styles.divHeight}>
            <Button
              onClick={handleShowChangePassword}
              name={showChangePass ? "Отменить изменение" : "Сменить пароль"}
              type="button"
              buttontStyle="default"
            />
          </div>
        )}
        {canChangePassword && showChangePass && (
          <>
            <div className={styles.formRow}>
              <ValidateInput
                showGenerateIcon={true}
                placeholder="Пароль"
                name="password"
                type="password"
                required={showChangePass}
                nameRu="Пароль"
                pattern={
                  /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%~^&_])[A-Za-z\d!@#$%~^&_]{10,}$/
                }
              />
            </div>
            {errors && errors.password && (
              <div className={styles.requireRules}>
                Пароль должен соответствовать требованиям:
                <ul>
                  <li>длина пароля 10 символов</li>
                  <li>не должен совпадать с предыдущим паролем</li>
                  <li>
                    должен включать символы: A-Z, a-z, "!", "@", "#", "$", "%",
                    "~", "^", "&", "_", 0-9
                  </li>
                </ul>
              </div>
            )}
            <div className={styles.formRow}>
              <ValidateInput
                placeholder="Подтверждение пароля"
                name="confirmPassword"
                type="password"
                required={showChangePass}
                nameRu="Подтверждение пароля"
              />
            </div>
            {errors && errors.confirmPassword && (
              <div className={styles.requireRules}>Пароли не совпадают</div>
            )}
          </>
        )}

        {/* ---------- Кнопки ---------- */}
        <div className={styles.formButtons}>
          <ResetPasswordButton currentUser={currentUser} roleName={roleName} />
          <div>
            <Button
              onClick={resetForm}
              name="Отменить"
              type="button"
              buttontStyle="cancelBtn"
            />
            <Button
              type="submit"
              name="Изменить"
              disabled={!methods.formState.isValid}
              buttontStyle="default"
            />
          </div>
        </div>
      </form>
    </FormProvider>
  );
};
